import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faTrashCan } from "@fortawesome/free-solid-svg-icons";
import { Button } from '@art-of-hospitality-solutions-llc/ui-kit/components/button';
import { isUpcomingScheduleComplete } from './UpcomingScheduleSection';

const FlowBuilderStageSidebar = ({ selectedStageIndex, currentFlowStages, setCurrentFlowStages, stageDepArray, setStageDepArray, flowStageTypes, typeDependencies, allTypeformForms, allCustomForms, handleOpenModal, canWrite, onEditConditions }) => {

  const handleAddNewFormSelector = (selectedStageIndex, depIndex, dep) => {
    setCurrentFlowStages((prevStages) => {
      return prevStages.map((stage, i) => {
        if (i !== selectedStageIndex) return stage;

        const depIdentifiers = [...stage.dependencyIdentifiers];
        let updatedDep;

        if (Array.isArray(depIdentifiers[depIndex])) {
          updatedDep = [...depIdentifiers[depIndex], ''];
        } else if (depIdentifiers[depIndex] !== undefined) {
          updatedDep = [depIdentifiers[depIndex], ''];
        } else {
          updatedDep = [''];
        }

        depIdentifiers[depIndex] = updatedDep;

        setStageDepArray((prevDepArray) => {
          prevDepArray[selectedStageIndex] = {
            ...prevDepArray[selectedStageIndex],
            [dep]: updatedDep
          };

          return prevDepArray
        });

        const updatedStage = {
          ...stage,
          dependencyIdentifiers: depIdentifiers
        };

        return updatedStage;
      });
    });
  };

  const handleRemoveLatestFormSelector = (selectedStageIndex, depIndex, dep) => {
    setCurrentFlowStages((prevStages) => {
      return prevStages.map((stage, i) => {
        if (i !== selectedStageIndex) return stage;

        const depIdentifiers = [...stage.dependencyIdentifiers];
        let updatedDep = depIdentifiers[depIndex];

        if (Array.isArray(updatedDep)) {
          if (updatedDep.length <= 2) {
            updatedDep = updatedDep[0] || '';
          } else {
            updatedDep = updatedDep.slice(0, -1);
          }
        } else {
          updatedDep = '';
        }

        depIdentifiers[depIndex] = updatedDep;

        setStageDepArray((prevDepArray) => {
          prevDepArray[selectedStageIndex] = {
            ...prevDepArray[selectedStageIndex],
            [dep]: updatedDep
          };

          return prevDepArray
        });

        const updatedStage = {
          ...stage,
          dependencyIdentifiers: depIdentifiers
        };

        return updatedStage;
      });
    });
  };

  const handleFormSelection = (e, dep, index) => {
    let selectedForm;
    if (dep === 'typeform') {
      selectedForm = allTypeformForms.find((form) => form.formId === e.target.value);
    } else if (dep === 'customForm') {
      selectedForm = allCustomForms.find((form) => form.id === e.target.value);
    }

    if (!selectedForm) return;

    const prevIdentifiers = stageDepArray[selectedStageIndex] || {};
    const updatedIdentifiers = { ...prevIdentifiers };

    if (Array.isArray(updatedIdentifiers[dep])) {
      updatedIdentifiers[dep] = [...updatedIdentifiers[dep]]; // clone array
      updatedIdentifiers[dep][index] = dep === 'typeform' ? selectedForm.formId : selectedForm.id;
    } else if (index > 0) {
      updatedIdentifiers[dep] = [updatedIdentifiers[dep]];
      updatedIdentifiers[dep][index] = dep === 'typeform' ? selectedForm.formId : selectedForm.id;
    } else {
      updatedIdentifiers[dep] = dep === 'typeform' ? selectedForm.formId : selectedForm.id;
    }

    setStageDepArray((prevDepArray) => {
      const newDepArray = [...prevDepArray];
      newDepArray[selectedStageIndex] = updatedIdentifiers;

      return newDepArray;
    });

    setCurrentFlowStages((prevStages) => {
      return prevStages.map((stage, mapIndex) => {
        if (mapIndex !== selectedStageIndex) return stage;

        const currentTypeDependencies = flowStageTypes.find(
          (type) => type.id === stage.flowStageTypeId
        )?.dependencies || [];


        const newDependencyIdentifiers = currentTypeDependencies.map(
          (depKey) => updatedIdentifiers[depKey]
        );

        return {
          ...stage,
          dependencyIdentifiers: newDependencyIdentifiers,
        };
      });
    });
  };

  const handleFlowStageTitleChange = (id, newTitle, i) => {
    setCurrentFlowStages(prevStages =>
      prevStages.map((stage, index) => {
        const updatedStage = (stage.id === id || index === i)
          ? { ...stage, title: newTitle }
          : stage;

        return updatedStage;
      })
    );
  };

  const handleFlowStageTypeChange = (e, id, newType, i) => {
    const typeDependenciesTemp = flowStageTypes?.find((type) => type.id === newType)?.dependencies || [];
    let newIdentifiers = {};
    if (typeDependenciesTemp.length) {
      for (const dep of typeDependenciesTemp) {
        newIdentifiers[dep] = 0;
      }
    }

    setStageDepArray((prevDepArray) => [
      ...prevDepArray.slice(0, i),
      newIdentifiers,
      ...prevDepArray.slice(i + 1)
    ])

    setCurrentFlowStages(prevStages =>
      prevStages.map((stage, index) => {
        const updatedStage = (stage.id === id || index === i)
          ? {
            ...stage,
            flowStageTypeId: newType,
            dependencyIdentifiers: []
          }
          : stage;

        return updatedStage;
      })
    );
  };

  // An upcoming stage can't be saved until its schedule names a date (and a send
  // time where that date has no time of day).
  const selectedStage = currentFlowStages[selectedStageIndex];
  const selectedUpcoming = flowStageTypes.find((type) => type.id === selectedStage?.flowStageTypeId)?.upcoming;
  const needsSchedule =
    !!selectedUpcoming && !isUpcomingScheduleComplete(selectedUpcoming, selectedStage?.conditions?.schedule);

  return (
    <div className="flex flex-col h-full justify-between bg-white rounded-r p-6 z-50 overflow-auto">
      {selectedStageIndex !== null ? (
        <div className='flex flex-col gap-4'>
          <h2 className="text-xl font-semibold mb-4">Configure Stage</h2>

          <div className='flex flex-col'>
            <h3 className="text-sm text-gray-500">Stage Title:</h3>
            <input
              type="text"
              placeholder="Stage name..."
              className="border px-3 py-2 rounded-md w-full"
              value={currentFlowStages[selectedStageIndex]?.title || ''}
              onChange={(e) => {
                handleFlowStageTitleChange(currentFlowStages[selectedStageIndex]?.id, e.target.value, selectedStageIndex)
              }}
            />
          </div>

          <div className='flex flex-col'>
            <h3 className="text-sm text-gray-500">Stage Type:</h3>
            <select
              className="border px-3 py-2 rounded-md w-full"
              id={'type-selector-dropdown'}
              value={currentFlowStages[selectedStageIndex]?.flowStageTypeId || ''}
              onChange={(e) => {
                handleFlowStageTypeChange(e, currentFlowStages[selectedStageIndex]?.id, e.target.value, selectedStageIndex)
              }}
            >
              <option value={''} disabled hidden className='opacity-10'>
                --Select Type--
              </option>
              {flowStageTypes.map((type, i) => {
                return (
                  <option value={type.id || ''} key={`type${i}-${currentFlowStages[selectedStageIndex]?.id}`}>
                    {type.name}
                  </option>
                )
              })}
            </select>
          </div>

          {currentFlowStages[selectedStageIndex]?.conditionsSummary && (
            <p className="text-sm text-zinc-500">
              {currentFlowStages[selectedStageIndex].conditionsSummary}
            </p>
          )}

          <Button
            type="button"
            variant="outline"
            className="w-full"
            onClick={onEditConditions}
          >
            Edit Conditions
          </Button>

          {needsSchedule && (
            <p className="text-sm text-amber-700">
              Set this stage's schedule under Edit Conditions before saving.
            </p>
          )}

          <h2 className="font-bold ">Dependencies</h2>
          {typeDependencies?.map((dep, depIndex) => {
            return (
              <div className='flex flex-col' key={`typeform-form-selector-${depIndex}`}>
                <h3 className="text-sm text-gray-500">{dep}:</h3>
                {['typeform'].includes(dep) && (
                  <div className='flex flex-col gap-2'>
                    <div
                      className={`flex flex-col items-center gap-2`}
                    >
                      {(Array.isArray(currentFlowStages[selectedStageIndex].dependencyIdentifiers[depIndex]) ? currentFlowStages[selectedStageIndex].dependencyIdentifiers[depIndex] : [currentFlowStages[selectedStageIndex].dependencyIdentifiers[depIndex]]).map((formId, index) => {
                        return (
                          <select
                            className="border px-3 py-2 rounded-md w-full"
                            key={`typeform-form-selector-${depIndex}-${index}-${formId}`}
                            value={formId || ''}
                            onChange={(e) => {
                              handleFormSelection(e, dep, index)
                            }}
                          >
                            <option value={''}>
                              --Select Associated Form--
                            </option>
                            {allTypeformForms.map((form) => (
                              <option value={form.formId} key={form.id}>
                                {form.formTitle}
                              </option>
                            ))}
                          </select>
                        )
                      })}
                    </div>
                    <div className='flex gap-2'>
                      <Button
                        variant='outline'
                        type='button'
                        onClick={() => handleAddNewFormSelector(selectedStageIndex, depIndex, dep)}
                        disabled={!canWrite}
                      >
                        <FontAwesomeIcon icon={faPlus} />
                      </Button>

                      <Button
                        variant='outline'
                        type='button'
                        onClick={() => handleRemoveLatestFormSelector(selectedStageIndex, depIndex, dep)}
                        disabled={!canWrite}
                      >
                        <FontAwesomeIcon icon={faTrashCan} />
                      </Button>
                    </div>

                  </div>
                )}

                {['customForm'].includes(dep) && (
                  <div key={`custom-form-selector-${depIndex}`} className='flex flex-col gap-2'>
                    <div
                      className={`flex flex-col items-center gap-2`}
                    >

                      {(Array.isArray(currentFlowStages[selectedStageIndex].dependencyIdentifiers[depIndex]) ? currentFlowStages[selectedStageIndex].dependencyIdentifiers[depIndex] : [currentFlowStages[selectedStageIndex].dependencyIdentifiers[depIndex]]).map((formId, index) => {
                        return (
                          <select
                            className="border px-3 py-2 rounded-md w-full"
                            key={`custom-form-selector-${depIndex}-${index}-${formId}`}
                            value={formId || ''}
                            onChange={(e) => {
                              handleFormSelection(e, dep, index)
                            }}>
                            <option value={''}>
                              --Select Associated Form--
                            </option>
                            {allCustomForms.map((form) => (
                              <option value={form.id} key={form.id}>
                                {form.formTitle}
                              </option>
                            ))}
                          </select>
                        )
                      })}
                    </div>

                    <div className='flex gap-2'>
                      <Button
                        variant='outline'
                        type='button'
                        onClick={() => handleAddNewFormSelector(selectedStageIndex, depIndex, dep)}
                        disabled={!canWrite}
                      >
                        <FontAwesomeIcon icon={faPlus} />
                      </Button>

                      <Button
                        variant='outline'
                        type='button'
                        onClick={() => handleRemoveLatestFormSelector(selectedStageIndex, depIndex, dep)}
                        disabled={!canWrite}
                      >
                        <FontAwesomeIcon icon={faTrashCan} />
                      </Button>
                    </div>

                  </div>
                )}
              </div>
            )
          })}
        </div>
      ) : (
        <div>
          <h2 className="text-xl font-semibold mb-4">Configure Stage</h2>
          <p className="text-sm text-gray-500 mb-2">Select a Stage</p>
        </div>
      )}

      {selectedStageIndex !== null &&
        <div className=''>
          <Button
            variant='destructive'
            type='button'
            onClick={() => { handleOpenModal('delete-stage') }}
            disabled={!canWrite}
          >
            Delete Stage
          </Button>
        </div>
      }

    </div>
  );
};

export default FlowBuilderStageSidebar;